/**
 * The whole squad, drawn on the pitch.
 *
 * The positions sheet answers "who starts"; this one answers "who have I got".
 * Each position carries everyone who plays there, in the order the depth chart
 * ranks them, so a coach can see the shape and the cover in the same glance —
 * and hand the page to a director, a parent or a goalkeeping coach without
 * explaining it.
 */

import { UNIT_FILL, drawPortraitPitch } from './formationCanvas';
import type { SheetHeader } from './formationCanvas';

export interface SquadSlot {
  positionKey: string;
  /** Fractional pitch coordinates, as the formation defines them. */
  x: number;
  y: number;
  unit?: 'gk' | 'defence' | 'midfield' | 'attack';
  /** First choice first. */
  players: { name: string; shirtNumber?: number | null; roleKey?: string | null }[];
}

export interface SquadSheet {
  slots: readonly SquadSlot[];
  /** Players who do not sit in the chosen shape. Listed rather than dropped. */
  unplaced: { name: string; shirtNumber?: number | null; positionKey?: string | null }[];
  unplacedLabel: string;
  header: SheetHeader;
}

/** Enough to read at arm's length on a printed page. */
function nameFont(size: number): string {
  return `500 ${size}px "IBM Plex Sans", sans-serif`;
}

/**
 * Draws the pitch and, at each position, the players who play there.
 *
 * The position token stays the size it is on the formation sheet so the two
 * pages read as the same picture; the names hang below it in a column, which is
 * the only arrangement that survives eleven positions on one page.
 */
export function drawSquadPitch(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  slots: readonly SquadSlot[],
) {
  drawPortraitPitch(ctx, width, height);

  const r = Math.max(11, Math.min(20, width * 0.021));
  const line = Math.max(9, Math.round(width * 0.0145));
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  for (const slot of slots) {
    const cx = slot.x * width;
    const cy = (1 - slot.y) * height;

    // The token: the position code, coloured by unit, as on the formation sheet.
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 5;
    ctx.shadowOffsetY = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = slot.unit ? (UNIT_FILL[slot.unit] ?? '#46566b') : '#46566b';
    ctx.fill();
    ctx.restore();

    ctx.fillStyle = '#ffffff';
    ctx.font = `600 ${Math.round(r * 0.78)}px "IBM Plex Sans Condensed", sans-serif`;
    ctx.fillText(slot.positionKey, cx, cy + 1);

    // The names, on a plate so they stay readable over the pitch markings.
    if (slot.players.length === 0) continue;
    const rows = slot.players.slice(0, 5);
    const widest = Math.max(
      ...rows.map((p) => {
        ctx.font = nameFont(line);
        return ctx.measureText(`${p.shirtNumber ?? ''} ${p.name}`.trim()).width;
      }),
    );
    const boxW = widest + line * 1.4;
    const boxH = rows.length * line * 1.42 + line * 0.5;
    const boxX = cx - boxW / 2;
    const boxY = cy + r + line * 0.45;

    ctx.save();
    ctx.fillStyle = 'rgba(255,255,255,0.93)';
    ctx.strokeStyle = 'rgba(16,26,40,0.18)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, boxW, boxH, 3);
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    rows.forEach((p, i) => {
      // First choice reads darker and heavier: a depth chart is an order.
      ctx.fillStyle = i === 0 ? '#101a28' : '#46566b';
      ctx.font = i === 0 ? `600 ${line}px "IBM Plex Sans", sans-serif` : nameFont(line);
      const text = p.shirtNumber ? `${p.shirtNumber}  ${p.name}` : p.name;
      ctx.fillText(text, cx, boxY + line * 0.85 + i * line * 1.42);
    });

    if (slot.players.length > rows.length) {
      ctx.fillStyle = '#6b7a8c';
      ctx.font = nameFont(line * 0.9);
      ctx.fillText(`+${slot.players.length - rows.length}`, cx, boxY + boxH + line * 0.7);
    }
  }
}

/**
 * The printable page: branded header, the pitch, and whoever does not fit the
 * shape listed underneath rather than quietly left out.
 */
export function drawSquadSheet(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  sheet: SquadSheet,
  dir: 'ltr' | 'rtl' = 'ltr',
) {
  const pad = Math.round(width * 0.055);
  const headerH = Math.round(height * 0.105);
  const footerH = Math.round(height * 0.04);
  const hasUnplaced = sheet.unplaced.length > 0;
  const unplacedH = hasUnplaced ? Math.round(height * 0.1) : 0;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  const start = dir === 'rtl' ? width - pad : pad;
  const end = dir === 'rtl' ? pad : width - pad;
  ctx.textAlign = dir === 'rtl' ? 'right' : 'left';
  ctx.textBaseline = 'alphabetic';

  ctx.fillStyle = '#101a28';
  ctx.font = `600 ${Math.round(headerH * 0.36)}px "IBM Plex Sans Condensed", sans-serif`;
  ctx.fillText(sheet.header.title, start, Math.round(headerH * 0.45));

  ctx.fillStyle = '#46566b';
  ctx.font = `500 ${Math.round(headerH * 0.24)}px "IBM Plex Sans", sans-serif`;
  ctx.fillText(sheet.header.phaseLabel, start, Math.round(headerH * 0.75));

  ctx.textAlign = dir === 'rtl' ? 'left' : 'right';
  ctx.font = `400 ${Math.round(headerH * 0.2)}px "IBM Plex Sans", sans-serif`;
  [sheet.header.teamName, sheet.header.coachName, sheet.header.formationKey, sheet.header.date]
    .filter(Boolean)
    .forEach((lineText, i) => {
      ctx.fillText(lineText, end, Math.round(headerH * 0.3) + i * Math.round(headerH * 0.24));
    });

  const pitchTop = headerH;
  const pitchH = height - headerH - footerH - unplacedH;
  ctx.save();
  ctx.translate(pad, pitchTop);
  drawSquadPitch(ctx, width - pad * 2, pitchH, sheet.slots);
  ctx.restore();

  if (hasUnplaced) {
    const y = pitchTop + pitchH + Math.round(unplacedH * 0.3);
    ctx.textAlign = dir === 'rtl' ? 'right' : 'left';
    ctx.fillStyle = '#46566b';
    ctx.font = `600 ${Math.round(unplacedH * 0.2)}px "IBM Plex Sans Condensed", sans-serif`;
    ctx.fillText(sheet.unplacedLabel, start, y);

    ctx.font = `400 ${Math.round(unplacedH * 0.185)}px "IBM Plex Sans", sans-serif`;
    ctx.fillStyle = '#6b7a8c';
    const names = sheet.unplaced
      .map((p) => (p.shirtNumber ? `${p.shirtNumber} ${p.name}` : p.name))
      .join(' · ');
    // One wrapped line is enough; a squad with thirty unplaced players has a
    // bigger problem than this sheet can solve.
    const maxWidth = width - pad * 2;
    let text = names;
    while (ctx.measureText(text).width > maxWidth * 2 && text.length > 20) {
      text = `${text.slice(0, Math.floor(text.length * 0.9))}…`;
    }
    wrapText(ctx, text, start, y + Math.round(unplacedH * 0.3), maxWidth, Math.round(unplacedH * 0.25), dir);
  }

  ctx.textAlign = dir === 'rtl' ? 'right' : 'left';
  ctx.fillStyle = '#9aa7b5';
  ctx.font = `400 ${Math.round(footerH * 0.42)}px "IBM Plex Sans", sans-serif`;
  ctx.fillText('MyPlannerPad', start, height - Math.round(footerH * 0.35));
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  dir: 'ltr' | 'rtl',
) {
  const words = text.split(' ');
  let line = '';
  let cursor = y;
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      ctx.fillText(line, x, cursor);
      cursor += lineHeight;
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) ctx.fillText(line, x, cursor);
  void dir;
}
